import React, { Component } from 'react';
import styled from '@emotion/styled';
import { SPACING } from 'Style/spacing';
import { BREAKPOINTS } from 'Style/breakpoints';

import connector from 'Utils/connector';
import connectAmp, {
  ConnectAmpProps,
} from 'Webapp/shared/app/connectors/connectAmp';
import connectFeatureFlags, {
  ConnectFeatureFlagsProps,
} from 'Webapp/shared/app/connectors/connectFeatureFlags';
import connectSendUsageEventWithSession from 'Webapp/shared/app/connectors/connectSendUsageEventWithSession';

import AdSlot, { AdSuppressedReason, sendAdSuppressedEvent } from './ad-slot';
import { MEDIUM_RECTANGLE_SIZES, isStoryboardAdEnabled } from './ads-config';
import { setPageTargeting } from './gpt';

type AdHintUnit = {
  type?: string;
  unit_id?: string;
  kvs?: Record<string, unknown>;
};

type StoryboardAdProps = ConnectAmpProps &
  ConnectFeatureFlagsProps & {
    section: Flipboard.Section;
    sendUsageEventWithSession?: (event: Record<string, unknown>) => void;
  };

// Framed, centered interstitial so the 300x250 reads as an intentional break
// in the editorial storyboard rather than a bare box floating in the themed
// (cover-derived, usually dark) background. Rendered as an <li> so it is a
// valid child of the storyboard groups <ul>. A translucent divider below
// separates it from the content that follows.
const StoryboardAdContainer = styled.li({
  listStyle: 'none',
  display: 'flex',
  justifyContent: 'center',
  ...BREAKPOINTS.tabletPortraitUp({
    paddingBottom: SPACING.BASE6X,
    marginBottom: SPACING.BASE6X,
    borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
  }),
  ...BREAKPOINTS.phone({
    paddingBottom: SPACING.LARGE,
    marginBottom: SPACING.LARGE,
    borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
  }),
});

const dfpUnit = (section: Flipboard.Section): AdHintUnit | undefined => {
  const adhints = (section as unknown as { adhints?: { units?: AdHintUnit[] } })
    ?.adhints;
  return adhints?.units?.find((u) => u.type === 'dfp' && !!u.unit_id);
};

/**
 * In-body ad for editorial Storyboards (package / `bundle` sections).
 *
 * Unlike the other web placements (which use build-time unit paths from
 * AD_UNIT_PATHS), the storyboard ad unit comes from the section's
 * server-provided `adhints` — the per-storyboard editorial unit GAM already
 * provisions (e.g. `/21709104563/flipboard/editorial/<slug>`). Renders nothing
 * unless WEB_ADS + WEB_ADS_STORYBOARD are on, the page is not AMP, AND the
 * section carries a `dfp` adhints unit; AdSlot itself self-gates on the flags.
 */
type StoryboardAdState = { empty: boolean };

export class StoryboardAd extends Component<
  StoryboardAdProps,
  StoryboardAdState
> {
  state: StoryboardAdState = { empty: false };

  componentDidMount() {
    this.applyPageTargeting();
    this.reportSuppressed();
  }

  // AMP is still checked here even though storyboard-layout only mounts this
  // component when !isAmp: defence for any future call site, since AMP has no
  // GPT lifecycle. It is deliberately NOT a suppression reason — see
  // reportSuppressed.
  shouldRender = () => !this.props.isAmp && this.suppressedReason() === null;

  /**
   * Why this storyboard will not request an ad, or null if it will.
   *
   * Ladder is flag_off before no_ad_unit: when storyboard ads are switched off
   * nothing would render whatever the section carries, so reporting
   * no_ad_unit there would invent a fleet of "missing ad unit" signals every
   * time someone flips a flag. no_ad_unit therefore means what it says — ads
   * are on for this placement and this storyboard still has no unit to ask for.
   */
  suppressedReason = (): AdSuppressedReason | null => {
    const { featureFlags, section } = this.props;
    if (!isStoryboardAdEnabled(featureFlags)) {
      return 'flag_off';
    }
    if (!dfpUnit(section)) {
      return 'no_ad_unit';
    }
    return null;
  };

  /**
   * A storyboard that renders no slot is otherwise completely silent: no
   * request, no outcome, nothing to distinguish "this storyboard has no ad
   * unit" from "nobody opened it". Reported once on mount.
   *
   * Skipped on AMP: the only call site never mounts this there, and an AMP
   * page has no GPT lifecycle to report a suppressed slot against.
   */
  reportSuppressed = () => {
    if (this.props.isAmp) {
      return;
    }
    const reason = this.suppressedReason();
    if (!reason) {
      return;
    }
    sendAdSuppressedEvent(this.props.sendUsageEventWithSession, {
      name: 'storyboard',
      reason,
    });
  };

  // On no-fill, collapse the whole framed block (container + divider) so an
  // unfilled storyboard leaves no gap in the editorial content, rather than the
  // reserved-height empty box the display slot keeps by default. The slot has
  // already made its request by the time this fires (slotRenderEnded), so
  // collapsing here does not suppress the ad request.
  handleSlotStatus = (filled: boolean) => {
    if (this.state.empty === filled) {
      this.setState({ empty: !filled });
    }
  };

  // GPT page-level targeting (page_type / topic_slug) is global. A storyboard
  // has no topic-page context, so clear any values left set by a prior SPA
  // page (topic/article) before this slot requests — otherwise the storyboard
  // ad inherits the previous page's stale key-values.
  applyPageTargeting = () => {
    if (!this.shouldRender()) {
      return;
    }
    setPageTargeting({ page_type: 'storyboard', topic_slug: null });
  };

  render() {
    if (!this.shouldRender() || this.state.empty) {
      return null;
    }
    const unit = dfpUnit(this.props.section);
    // Forward the server-provided key-values (owner, sectype, section_id, ...)
    // as slot targeting.
    const targeting: Record<string, string> = { page_type: 'storyboard' };
    Object.entries(unit?.kvs || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        targeting[key] = String(value);
      }
    });

    return (
      <StoryboardAdContainer className="storyboard-ad">
        <AdSlot
          name="storyboard"
          placementFlag="WEB_ADS_STORYBOARD"
          adUnitPath={unit?.unit_id as string}
          sizes={MEDIUM_RECTANGLE_SIZES}
          targeting={targeting}
          showLabel
          lazy
          onSlotStatus={this.handleSlotStatus}
        />
      </StoryboardAdContainer>
    );
  }
}

export default connector<StoryboardAdProps>(
  connectAmp,
  connectFeatureFlags,
  connectSendUsageEventWithSession,
)(StoryboardAd);
